import React, { useCallback, useMemo, useRef, useState } from 'react'
import { exportImportManager } from '../../lib/exportImportManager'
import { APP_STATE_BACKUP_BUSY_MESSAGE } from '../../lib/appStateBackupProgress'
import { useAppStateBackupProgress } from './AppStateBackupProgressOverlay'

export interface AppStateBackupStatus {
  kind: 'success' | 'error'
  message: string
}

interface AppStateBackupControlsProps {
  allowImport?: boolean
  exportLabel?: string
  importLabel?: string
  containerClassName?: string
  exportButtonClassName: string
  importButtonClassName?: string
  disabled?: boolean
  filenamePrefix?: string
  reloadOnImport?: boolean
  onStatus?: (status: AppStateBackupStatus) => void
}

export const DEFAULT_APP_STATE_BACKUP_FILENAME_PREFIX = 'segmentation-agent-app-backup'

export const buildAppStateBackupFilename = (prefix: string, extension: 'json' | 'zip'): string => (
  `${prefix}-${new Date().toISOString().slice(0, 10)}.${extension}`
)

export const triggerAppStateBackupDownload = (content: string | Blob, filename: string): void => {
  const blob = typeof content === 'string'
    ? new Blob([content], { type: 'application/json' })
    : content
  const objectUrl = window.URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  window.URL.revokeObjectURL(objectUrl)
}

export const readAppStateBackupPayload = async (
  file: File,
  onProgress?: (loaded: number, total: number) => void
): Promise<string | ArrayBuffer> => {
  const isZip = file.name.toLowerCase().endsWith('.zip') || file.type === 'application/zip'

  if (typeof FileReader === 'undefined') {
    const result = isZip && typeof file.arrayBuffer === 'function'
      ? await file.arrayBuffer()
      : await file.text()
    onProgress?.(file.size, file.size)
    return result
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      onProgress?.(file.size, file.size)
      resolve(reader.result as string | ArrayBuffer)
    }
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'))
    reader.onabort = () => reject(new Error('Reading the backup file was cancelled.'))
    reader.onprogress = event => onProgress?.(event.loaded, event.lengthComputable ? event.total : file.size)
    if (isZip) {
      reader.readAsArrayBuffer(file)
    } else {
      reader.readAsText(file)
    }
  })
}

export const applyAppStateBackupRouteSnapshot = (routeSnapshot: { pathname: string; search: string; hash: string } | null): void => {
  if (!routeSnapshot) {
    return
  }
  const pathname = routeSnapshot.pathname || window.location.pathname
  const search = routeSnapshot.search || ''
  const hash = routeSnapshot.hash || ''
  window.history.replaceState(null, '', `${pathname}${search}${hash}`)
}

export function AppStateBackupControls({
  allowImport = false,
  exportLabel = 'Backup App',
  importLabel = 'Import App Backup',
  containerClassName,
  exportButtonClassName,
  importButtonClassName,
  disabled = false,
  filenamePrefix = DEFAULT_APP_STATE_BACKUP_FILENAME_PREFIX,
  reloadOnImport = true,
  onStatus,
}: AppStateBackupControlsProps) {
  const [exporting, setExporting] = useState(false)
  const [importing, setImporting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const backupProgress = useAppStateBackupProgress()

  const resolvedImportButtonClassName = useMemo(
    () => importButtonClassName || exportButtonClassName,
    [exportButtonClassName, importButtonClassName]
  )

  const emitStatus = useCallback((status: AppStateBackupStatus) => {
    onStatus?.(status)
  }, [onStatus])

  const handleExport = useCallback(async () => {
    if (disabled || exporting || importing) {
      return
    }
    const operationToken = backupProgress.begin('export')
    if (!operationToken) {
      emitStatus({ kind: 'error', message: APP_STATE_BACKUP_BUSY_MESSAGE })
      return
    }
    setExporting(true)
    const reportProgress = (event: Parameters<typeof backupProgress.report>[1]) => {
      backupProgress.report(operationToken, event)
    }
    try {
      const exported = await exportImportManager.exportAppState('zip', reportProgress)
      triggerAppStateBackupDownload(exported, buildAppStateBackupFilename(filenamePrefix, 'zip'))
      backupProgress.complete(operationToken, 'export', 'Backup download started')
      emitStatus({
        kind: 'success',
        message: 'Backup download started. Saved matrix sessions and server Build runs require a separate server backup.'
      })
    } catch (error) {
      backupProgress.fail(operationToken, 'export', error instanceof Error ? error.message : 'Please try the backup again.')
      emitStatus({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Failed to export app backup.'
      })
    } finally {
      setExporting(false)
    }
  }, [backupProgress, disabled, emitStatus, exporting, filenamePrefix, importing])

  const handleImportFile = useCallback(async (file: File) => {
    if (disabled || exporting || importing) {
      return
    }
    const operationToken = backupProgress.begin('import')
    if (!operationToken) {
      emitStatus({ kind: 'error', message: APP_STATE_BACKUP_BUSY_MESSAGE })
      return
    }
    setImporting(true)
    const reportProgress = (event: Parameters<typeof backupProgress.report>[1]) => {
      backupProgress.report(operationToken, event)
    }
    try {
      const content = await readAppStateBackupPayload(file, (loaded, total) => {
        const ratio = total > 0 ? Math.max(0, Math.min(1, loaded / total)) : 0
        reportProgress({
          operation: 'import',
          phase: 'reading-file',
          percent: 1 + (ratio * 5),
          message: 'Reading the selected backup',
          detail: total > 0 ? `${Math.round(ratio * 100)}% of the file read.` : 'Reading the backup file.',
        })
      })
      const { result, routeSnapshot } = await exportImportManager.importAppStateBackup(content, reportProgress)
      if (!result.success) {
        const message = result.errors[0]?.message || 'Failed to import app backup.'
        backupProgress.fail(operationToken, 'import', message)
        emitStatus({ kind: 'error', message })
        return
      }
      applyAppStateBackupRouteSnapshot(routeSnapshot)
      const canReload = backupProgress.complete(operationToken, 'import', 'Your workspace has been restored', {
        persistAcrossReload: reloadOnImport,
      })
      emitStatus({
        kind: 'success',
        message: reloadOnImport && canReload
          ? 'Imported app backup. Reloading to restore the saved state.'
          : reloadOnImport
            ? 'Imported app backup. Automatic reload was skipped so the confirmation would not disappear. Reload the page when you are ready.'
          : 'Imported app backup.'
      })
      if (reloadOnImport && canReload) {
        // Completion is rehydrated from a compact browser marker after reload,
        // so there is no need to leave a window in which another operation can start.
        window.location.reload()
      }
    } catch (error) {
      backupProgress.fail(operationToken, 'import', error instanceof Error ? error.message : 'Please choose the backup and try again.')
      emitStatus({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Failed to import app backup.'
      })
    } finally {
      setImporting(false)
    }
  }, [backupProgress, disabled, emitStatus, exporting, importing, reloadOnImport])

  return (
    <div className={containerClassName}>
      <input
        ref={fileInputRef}
        type="file"
        data-testid="app-state-backup-file-input"
        className="hidden"
        accept="application/json,.json,application/zip,.zip"
        onChange={event => {
          const file = event.currentTarget.files?.[0]
          if (file) {
            void handleImportFile(file)
          }
          event.currentTarget.value = ''
        }}
      />
      <button
        type="button"
        onClick={() => void handleExport()}
        disabled={disabled || exporting || importing || backupProgress.isBusy}
        className={exportButtonClassName}
      >
        {exporting ? 'Backing up…' : exportLabel}
      </button>
      {allowImport && (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || exporting || importing || backupProgress.isBusy}
          className={resolvedImportButtonClassName}
        >
          {importing ? 'Importing…' : importLabel}
        </button>
      )}
    </div>
  )
}

export default AppStateBackupControls
